import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ChargeBasisService } from '../../../../../services/forwarding/charge-basis.service';
import { ChargeBasis } from '../../../../../models/forwarding/charge-basis.model';
import { OperationalChargesService } from '../../../../../services/forwarding/operational-charges';
import { OperationalCharge } from '../../../../../models/forwarding/operational-charge.model';
import { ContainerLoadTypesService, ContainerLoadType } from '../../../../../services/forwarding/container-load-types.service';
import { AgentService } from '../../../../../services/forwarding/agent.service';
import { Agent } from '../../../../../models/forwarding/agent.model';
import { PortService } from '../../../../../services/forwarding/port.service';
import { Port } from '../../../../../models/forwarding/port.model';

// Dynamic load type rates - will be populated from service
type LoadTypeRates = Record<string, number>;

interface ChargeWithRates {
  chargeBasis: string;
  singleValue: number;  // Used when per-load-type is not expanded
  loadTypes: LoadTypeRates;
}

@Component({
  selector: 'app-add-port-charge-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './add-port-charge-modal.html',
  styleUrl: './add-port-charge-modal.css'
})
export class AddPortChargeModalComponent implements OnInit, OnDestroy {
  @Input() show = false;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();

  // Services data
  chargeBasisList: ChargeBasis[] = [];
  airOperationalCharges: OperationalCharge[] = [];
  seaOperationalCharges: OperationalCharge[] = [];
  containerLoadTypes: ContainerLoadType[] = [];
  agentsList: Agent[] = [];
  portsList: Port[] = [];
  private subscriptions = new Subscription();

  // Per-load-type toggle state for each charge
  perLoadTypeExpanded = {
    handling: false,
    customsClearance: false,
    truckingFee: false,
    exportLicense: false,
    cfs: false,
    ebsCscDocs: false,
    vgm: false,
    manifest: false,
    ediLss: false,
    depotFee: false,
    eir: false
  };

  // Form fields
  formData = {
    chargeType: 'Origin',
    agent: '',
    portList: '',
    validUntilDate: '',
    currency: 'USD',

    // Container-Specific Charges with Load Type Rates
    handling: this.createEmptyChargeWithRates(),
    customsClearance: this.createEmptyChargeWithRates(),
    truckingFee: this.createEmptyChargeWithRates(),
    exportLicense: this.createEmptyChargeWithRates(),

    // Standard Charges with Load Type Rates
    cfs: this.createEmptyChargeWithRates(),
    ebsCscDocs: this.createEmptyChargeWithRates(),
    vgm: this.createEmptyChargeWithRates(),
    manifest: this.createEmptyChargeWithRates(),
    ediLss: this.createEmptyChargeWithRates(),
    depotFee: this.createEmptyChargeWithRates(),
    eir: this.createEmptyChargeWithRates(),

    isActive: true
  };

  // Dropdown options
  chargeTypes = ['Origin', 'Destination', 'Transit'];
  currencies = ['USD', 'PHP', 'CNY', 'EUR'];

  // Operational charges expand/collapse
  airOperationalExpanded = false;
  seaOperationalExpanded = false;

  constructor(
    private chargeBasisService: ChargeBasisService,
    private operationalChargesService: OperationalChargesService,
    private containerLoadTypesService: ContainerLoadTypesService,
    private agentService: AgentService,
    private portService: PortService
  ) {}

  ngOnInit(): void {
    console.log('[AddPortChargeModal] Component initialized');
    this.loadChargeBasis();
    this.loadOperationalCharges();
    this.loadContainerLoadTypes();
    this.loadAgents();
    this.loadPorts();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private createEmptyChargeWithRates(): ChargeWithRates {
    const loadTypes: LoadTypeRates = {};
    // Will be populated dynamically from containerLoadTypes
    this.containerLoadTypes.forEach(lt => {
      loadTypes[lt.code.toLowerCase()] = 0;
    });

    return {
      chargeBasis: '',
      singleValue: 0,
      loadTypes
    };
  }

  private loadChargeBasis(): void {
    this.subscriptions.add(
      this.chargeBasisService.getCharges().subscribe(basisList => {
        this.chargeBasisList = basisList.filter(cb => cb.isActive);
        console.log('[AddPortChargeModal] Loaded charge basis:', this.chargeBasisList.length);
      })
    );
  }

  private loadOperationalCharges(): void {
    this.subscriptions.add(
      this.operationalChargesService.getCharges().subscribe(charges => {
        // Filter Air charges
        this.airOperationalCharges = charges.filter(c =>
          c.isActive && (c.mode === 'AIR' || c.mode === 'ALL')
        );

        // Filter Sea charges (both LCL and FCL)
        this.seaOperationalCharges = charges.filter(c =>
          c.isActive && (c.mode === 'SEA_LCL' || c.mode === 'SEA_FCL' || c.mode === 'ALL')
        );

        console.log('[AddPortChargeModal] Loaded operational charges - Air:',
          this.airOperationalCharges.length, 'Sea:', this.seaOperationalCharges.length);
      })
    );
  }

  private loadContainerLoadTypes(): void {
    this.subscriptions.add(
      this.containerLoadTypesService.getLoadTypes().subscribe(loadTypes => {
        this.containerLoadTypes = loadTypes;
        console.log('[AddPortChargeModal] Loaded container load types:', this.containerLoadTypes.length);
      })
    );
  }

  private loadAgents(): void {
    this.subscriptions.add(
      this.agentService.getAgents().subscribe({
        next: (agents) => {
          this.agentsList = agents.filter(a => a.isActive);
          console.log('[AddPortChargeModal] Loaded agents:', this.agentsList.length);
        },
        error: (error) => {
          console.error('[AddPortChargeModal] Error loading agents:', error);
        }
      })
    );
  }

  private loadPorts(): void {
    this.subscriptions.add(
      this.portService.getPorts().subscribe({
        next: (ports) => {
          this.portsList = ports.filter(p => p.isActive);
          console.log('[AddPortChargeModal] Loaded ports:', this.portsList.length);
        },
        error: (error) => {
          console.error('[AddPortChargeModal] Error loading ports:', error);
        }
      })
    );
  }

  togglePerLoadType(chargeName: keyof typeof this.perLoadTypeExpanded): void {
    this.perLoadTypeExpanded[chargeName] = !this.perLoadTypeExpanded[chargeName];
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    const saveData = {
      ...this.formData
    };
    console.log('[AddPortChargeModal] Saving port charge:', saveData);
    this.save.emit(saveData);
  }

  toggleAirOperational(): void {
    this.airOperationalExpanded = !this.airOperationalExpanded;
  }

  toggleSeaOperational(): void {
    this.seaOperationalExpanded = !this.seaOperationalExpanded;
  }

  getChargeBasisNotes(displayName: string): string | undefined {
    return this.chargeBasisList.find(b => b.displayName === displayName)?.notes;
  }

  // Helper method to get load type property key from code
  getLoadTypeKey(code: string): string {
    return code.toLowerCase();
  }
}
